import { useCallback, CSSProperties, useRef, useState } from 'react';

import { useTheme } from '@mui/material';

import { DsShadow, tokens } from '@cast/design-system';

type UseNewChatAnimationOptions = {
  enabled: boolean;
  onAnimationEnd: () => void;
};

export const useNewChatAnimation = ({
  enabled,
  onAnimationEnd,
}: UseNewChatAnimationOptions) => {
  const { shadows } = useTheme();
  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = useState(false);
  const [animationOrigin, setAnimationOrigin] = useState('50% 50%');

  const triggerAnimation = useCallback(() => {
    if (animating) return;
    if (enabled && historyBtnRef.current && messagesContainerRef.current) {
      const btnRect = historyBtnRef.current.getBoundingClientRect();
      const containerRect =
        messagesContainerRef.current.getBoundingClientRect();
      const originX = btnRect.left + btnRect.width / 2 - containerRect.left;
      const originY = btnRect.top + btnRect.height / 2 - containerRect.top;
      setAnimationOrigin(`${originX}px ${originY}px`);
      setAnimating(true);
      setTimeout(() => {
        setAnimating(false);
        onAnimationEnd();
      }, 600);
    } else {
      onAnimationEnd();
    }
  }, [enabled, onAnimationEnd, animating]);

  const containerStyle: CSSProperties = animating
    ? {
        transformOrigin: animationOrigin,
        transform: 'scale(0)',
        transition: 'transform 0.6s cubic-bezier(.64,0,.8,1)',
        pointerEvents: 'none',
        borderRadius: '4px',
        background: tokens.theme.bg.structural.surface,
        boxShadow: shadows[DsShadow.SHADOW],
      }
    : {
        transformOrigin: animationOrigin,
        transform: 'scale(1)',
      };

  return {
    historyBtnRef,
    messagesContainerRef,
    animating,
    containerStyle,
    triggerAnimation,
  };
};
